import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bell, Check, Trash2 } from 'lucide-react';

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { axiosInstance } = useAuth();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axiosInstance.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      toast.error('Échec du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await axiosInstance.put(`/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      toast.error('Échec de la mise à jour');
    }
  };

  const handleClearAll = async () => {
    try {
      await axiosInstance.delete('/notifications');
      toast.success('Notifications supprimées');
      fetchNotifications();
    } catch (error) {
      toast.error('Échec de la suppression');
    }
  };

  const getTypeBadge = (type) => {
    const variants = {
      approval: 'bg-green-500 text-white',
      rejection: 'bg-red-500 text-white',
      message: 'bg-blue-500 text-white',
      schedule: 'bg-purple-500 text-white',
      exchange: 'bg-orange-500 text-white',
      payslip: 'bg-amber-500 text-white'
    };
    return <Badge className={variants[type]}>{type}</Badge>;
  };

  if (loading) {
    return (
      <Layout title="Notifications">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Notifications">
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex justify-end">
          <Button
            onClick={handleClearAll}
            variant="outline"
            className="border-amber-600 text-amber-700"
            data-testid="clear-notifications-button"
          >
            <Trash2 size={16} className="mr-2" />
            Supprimer toutes les notifications lues
          </Button>
        </div>

        {/* Notifications List */}
        <div className="space-y-4" data-testid="notifications-list">
          {notifications.length === 0 ? (
            <Card className="glass-card border-amber-200">
              <CardContent className="py-12 text-center text-gray-500">
                Aucune notification
              </CardContent>
            </Card>
          ) : (
            notifications.map((notification) => (
              <Card
                key={notification.id}
                className={`glass-card border-amber-200 ${!notification.read_status ? 'bg-amber-50' : ''}`}
                data-testid={`notification-card-${notification.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="mt-1">
                        <Bell className={notification.read_status ? 'text-gray-400' : 'text-amber-600'} size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getTypeBadge(notification.type)}
                          {!notification.read_status && (
                            <Badge className="bg-red-500 text-white">Non lu</Badge>
                          )}
                        </div>
                        <p className="text-gray-900" data-testid={`notification-message-${notification.id}`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(notification.timestamp).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    
                    {!notification.read_status && (
                      <Button
                        onClick={() => handleMarkRead(notification.id)}
                        size="sm"
                        variant="outline"
                        className="border-green-600 text-green-700"
                        data-testid={`mark-read-${notification.id}`}
                      >
                        <Check size={16} className="mr-1" />
                        Marquer comme lu
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}